import httpStatus from 'http-status';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import callApi from '../utils/call-api.js';
import analyzeApiResponse from '../utils/analyze-api-response.js';
import { buildMockExecutivePulseDashboard } from '../data/mockDashboardData.js';

/**
 * Loads Executive Pulse pillar KPI snapshot from Oracle API (`GET /executive-pulse/dashboard`).
 * In development, falls back to bundled mock data when the request fails (not on 403).
 *
 * @param {number} year — calendar reporting year
 */
const useExecutivePulse = (year) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const load = useCallback(
        async (options = {}) => {
            const silent = options.silent === true;

            if (!silent) {
                setLoading(true);
                setForbidden(false);
                setErrorMessage(null);
                setData(null);
            }

            const qs = new URLSearchParams({
                year: String(year)
            });

            try {
                const response = await callApi(
                    `/executive-pulse/dashboard?${qs.toString()}`,
                    'get'
                );

                if (response.status === httpStatus.FORBIDDEN) {
                    if (!silent) {
                        setForbidden(true);
                    } else {
                        toast.error(
                            'You do not have permission to refresh this dashboard.'
                        );
                    }
                    return null;
                }

                if (!response.ok) {
                    const msg = await analyzeApiResponse(response);
                    throw new Error(
                        msg || 'Failed to load Executive Pulse data'
                    );
                }

                const payload = await response.json();
                setData(payload);
                return payload;
            } catch (err) {
                const isDev = process.env.REACT_APP_ENV === 'development';
                const msg =
                    err?.message || 'Failed to load Executive Pulse data';

                if (isDev) {
                    const mock = buildMockExecutivePulseDashboard(year);
                    if (!silent) {
                        toast.error(
                            `${msg} — showing mock data (development only).`
                        );
                    }
                    setData(mock);
                    return mock;
                }

                if (silent) {
                    toast.error(msg);
                    return null;
                }

                setErrorMessage(msg);
                return null;
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [year]
    );

    useEffect(() => {
        load();
    }, [load]);

    const reload = useCallback(() => load(), [load]);
    const refresh = useCallback(() => load({ silent: true }), [load]);

    return {
        data,
        loading,
        forbidden,
        errorMessage,
        reload,
        refresh
    };
};

export default useExecutivePulse;
